import React, { Dispatch, useContext, useEffect, useMemo, useState } from 'react';
import { PostEditorModalView, PostEditorModalViewProps } from './PostEditorModal.view';
import { useKeypress } from '../../../hooks/useKeypress';
import { Modal } from '../../../components/containers/Modal';
import { AppContext } from '../../../providers/AppProvider';
import { ButtonColorsEnum } from '../../../constants/button-colors';
import { DisconnectedProfilesType, PostEditorContextEnum, PostEditorState } from '../../../models/post-editor';
import { idGenerator } from '../../../helpers/idGenerator';
import { BreakpointsValuesEnum } from '../../../constants/breakpoints-values';
import { useCallbackPrompt } from '../../../hooks/useCallbackPrompt';
import { useBreakpoints } from '../../../hooks/useBreakpoints';
import { useDidMount } from '../../../hooks/useDidMount';
import { ModalsContext } from '../../../providers/ModalsProvider';
import { SubmitHandler } from 'react-hook-form/dist/types/form';
import { FieldValues, SubmitErrorHandler } from 'react-hook-form';
import { PostEditorPublicAction } from '../states/PostEditorActions';
import { PostEditorContext } from '../states/PostEditorProvider';
import { SocialProfileWithBoardsAndMentionType } from '../helpers/mapSelectedProfilesValues';
import { PostSource } from '../../../models/graphql';
import { BoxNotification } from '../../../components/containers/BoxNotification';
import { OnboardingContext } from '../../Onboarding/states/OnboardingProvider';
import { showToast } from '../../../helpers/showToast';
import pluralize from 'pluralize';
import { getCachedProfiles, SocialProfileInputValue } from '../helpers/getCachedProfiles';
import { isSocialProfileTypenameGuard } from '../helpers/guards';
import { useClosePostEditor } from '../../../hooks/useClosePostEditor';
import { useOnboardingWidgetHeight } from '../../../hooks/useOnboardingWidgetHeight';

export type OnSubmitPostEditorFunction<T = SubmitHandler<FieldValues>> = (
  dispatch: Dispatch<PostEditorPublicAction>,
  profiles: SocialProfileWithBoardsAndMentionType[],
  state: PostEditorState
) => T;

export type OnSubmitErrorPostEditorFunction<T = SubmitErrorHandler<FieldValues>> = (
  dispatch: Dispatch<PostEditorPublicAction>
) => T;

export type PostEditorModalControllerProps = {
  children: PostEditorModalViewProps['children'];
  loading: PostEditorModalViewProps['loading'];
  title: PostEditorModalViewProps['title'];
  formInstance: PostEditorModalViewProps['formInstance'];
  onSubmitHandler: OnSubmitPostEditorFunction<PostEditorModalViewProps['onSubmit']>;
  onErrorHandler: OnSubmitErrorPostEditorFunction<PostEditorModalViewProps['onError']>;
  setInitialised?: PostEditorModalViewProps['setInitialised'];
};

export const PostEditorModalController = (props: PostEditorModalControllerProps): JSX.Element => {
  // Put All your methods and logic here
  const [exit, setExit] = useState(false); //animation in/out state before we call unmount with props.onClose()
  const [animate, setAnimate] = useState<boolean>(true);
  const [confirmationModal, setConfirmationModal] = useState<boolean>(false);
  const { postEditorContext } = useContext(AppContext);
  const onClosePostEditor =
    postEditorContext.type !== PostEditorContextEnum.undefined ? postEditorContext.onClose : undefined;
  const modalId = useMemo(() => idGenerator('modal'), []);
  const breakpoint = useBreakpoints();
  const [modalsRef, modalsUpdater] = useContext(ModalsContext);
  const { dispatch, profiles, state } = useContext(PostEditorContext);
  const { showOnboarding } = useContext(OnboardingContext);
  const { disconnectedProfiles } = state;
  const { closePostEditor } = useClosePostEditor();
  const [showDisconnectedProfiles, setShowDisconnectedProfiles] =
    useState<DisconnectedProfilesType[]>(disconnectedProfiles);
  const onCloseAttempt = () => {
    setConfirmationModal(true);
  };
  const fullscreenBreakpoints: Array<keyof typeof BreakpointsValuesEnum> = [
    'small',
    'medium',
    'tabletportrait',
    'large',
    'xlarge'
  ];

  const { onErrorHandler, onSubmitHandler } = props;

  const onError = useMemo(() => onErrorHandler(dispatch), [dispatch, onErrorHandler]);
  const onSubmit = useMemo(
    () => onSubmitHandler(dispatch, profiles, state),
    [dispatch, state, onSubmitHandler, profiles]
  );

  const { showPrompt: isConfirmingNavigation, confirmNavigation, cancelNavigation } = useCallbackPrompt(modalId);
  const shouldShowConfirmationModal = isConfirmingNavigation || confirmationModal;

  const keepItOpen = (showPrompt: boolean) => {
    if (showPrompt) {
      cancelNavigation();
    }
    setConfirmationModal(false);
  };

  const confirmClose = (showPrompt: boolean) => {
    if (showPrompt) {
      // if we pressed back button we need to check if we are on fullscreen. If yes, we prevent navigation
      // and show confirmation dialog
      if (showOnboarding) {
        confirmNavigation();
      } else if (fullscreenBreakpoints.includes(breakpoint)) {
        // if we are on onboarding we have navigation widget, so we don't want to cancel navigation
        cancelNavigation();
      } else {
        confirmNavigation();
      }
    }
    setConfirmationModal(false);
    setAnimate(false);
  };

  const onboardingWidgetHeight = useOnboardingWidgetHeight();

  useDidMount(() => {
    if (modalId && !modalsRef.current.filter(item => item.id === modalId).length) {
      modalsUpdater({ type: 'add', value: modalId })(modalsRef);
    }
    return () => {
      modalsUpdater({ type: 'remove', value: modalId })(modalsRef);
    };
  });

  // once the exit animation is done, we can unmount the component
  useEffect(() => {
    if (exit) {
      onClosePostEditor && onClosePostEditor(false);
      closePostEditor();
    }
    return () => {
      // Cleanup function to redirect to /close_post_editor if we came here through the mobile app.
      // This is for the app to listen to & close the webview.
      if (exit && state.postGroups[0]?.groupPostAttributes.postSource === PostSource.API) {
        window.location.href = '/close_post_editor';
      }
    };
  }, [exit, onClosePostEditor, state.postGroups, closePostEditor]);

  useEffect(() => {
    setShowDisconnectedProfiles(old =>
      JSON.stringify(disconnectedProfiles) !== JSON.stringify(old) ? disconnectedProfiles : old
    );
  }, [disconnectedProfiles]);

  // trigger disconnected profiles modal
  useEffect(() => {
    const profilesToShow = showDisconnectedProfiles.filter(profile => profile.selected && !profile.shown);
    if (profilesToShow.length) {
      const profileInputArgs: SocialProfileInputValue[] = profilesToShow.flatMap(p =>
        isSocialProfileTypenameGuard(p.__typename) ? { id: p.id, type: p.__typename } : []
      );
      const profileFragments = getCachedProfiles(profileInputArgs);
      showToast({
        severity: 'warn',
        summary: `${pluralize('Profile', profileFragments.length, true)} Disconnected`,
        detail: (
          <>
            <p>Please reconnect the following {`${pluralize('profile', profileFragments.length)}`}:</p>
            <ul className={'no-b-m'}>
              {profileFragments.map(p => (
                <li key={p.id}>{p.displayName}</li>
              ))}
            </ul>
          </>
        ),
        sticky: false
      });
      dispatch({ type: 'setDisconnectedProfileToShown' });
    }
  }, [showDisconnectedProfiles, dispatch]);

  const onEscapeHandler = (e?: KeyboardEvent): void => {
    if (!props.loading) {
      const lastModalRef = modalsRef.current[modalsRef.current.length - 1];
      if (lastModalRef.id === modalId) {
        e && e.stopImmediatePropagation();
        onCloseAttempt();
      }
    }
  };

  useKeypress('Escape', onEscapeHandler);

  const viewProps: PostEditorModalViewProps = {
    ...props,
    onboardingWidgetHeight,
    loading: props.loading || state.loading,
    animate,
    setExit,
    onClose: onCloseAttempt,
    onError,
    onSubmit
  };
  return (
    <>
      <PostEditorModalView {...viewProps}>{viewProps.children}</PostEditorModalView>
      {shouldShowConfirmationModal && (
        <Modal
          onClose={() => keepItOpen(isConfirmingNavigation)}
          size={'small'}
          showCloseIcon={false}
          title={'Close Post Editor'}
          closeOnClickOutside={false}
          closeOnEsc
          primaryButtonAction={() => confirmClose(isConfirmingNavigation)}
          primaryButtonColor={ButtonColorsEnum.alert}
          primaryButtonTitle={'Close Post Editor'}
          secondaryButtonAction={() => keepItOpen(isConfirmingNavigation)}
          secondaryButtonColor={ButtonColorsEnum.secondary}
          secondaryButtonTitle={'Keep it Open'}>
          <p>Are you sure?</p>
          <BoxNotification type={'error'}>You will lose any changes that you&apos;ve made.</BoxNotification>
        </Modal>
      )}
    </>
  );
};
